<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import SeatsTable from '@/components/Admin/SeatsTable.vue'
import UIKit from '@pocketprep/ui-kit'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { licenseModule } from '@/store/license/module'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'
import { analyticsModule } from '@/store/analytics/module'

const {
    Pie,
    Button: PocketButton,
} = UIKit

const showPurchaseSeatsModal = ref(false)

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrglicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const showPurchaseSeatsButton = computed(() => {
    return organizationModule.getters.getHasSelfServicePaymentAccess()
})

const examLicenseMetrics = computed(() => {
    return currentOrglicenses.value.reduce(
        (acc, license) => {
            if (!license.examGuid) {
                return acc
            }

            if (license.status === 'available') {
                acc.available++
                acc.count++
            } else if (license.status === 'pending') {
                acc.pending++
                acc.count++
            } else if (license.status === 'consumed') {
                acc.active++
                acc.count++
            }

            return acc
        },
        { count: 0, available: 0, pending: 0, active: 0 }
    )
})

const pieData = computed(() => {
    return [{
        y: (examLicenseMetrics.value.active / examLicenseMetrics.value.count) * 100,
    }, {
        y: (examLicenseMetrics.value.pending / examLicenseMetrics.value.count) * 100,
    }, {
        y: (examLicenseMetrics.value.available / examLicenseMetrics.value.count) * 100,
    }]
})

onMounted(async () => {
    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
})

const showPurchaseSeatsSidePanel = () => {
    showPurchaseSeatsModal.value = true

    if (currentOrganization.value) {
        // Fire amplitude event
        analyticsModule.actions.amplitudeTrack('Side Panel Opened', {
            sidePanelName: 'Purchase Seats',
            sidePanelPlacement: 'Seats',
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })
    }
}
</script>

<template>
    <div class="seats">
        <PurchaseSeatsModal
            v-if="showPurchaseSeatsModal"
            @close="showPurchaseSeatsModal = false"
        />
        <div class="seats__title">
            Student Seats
        </div>
        <div class="seats__data">
            <SeatsTable
                class="seats__table"
                :show-full-exam-name="true"
                :enable-hover-on-row="false"
            />
            <div
                v-if="examLicenseMetrics.count > 0"
                class="seats__graph"
                :class="{ 'seats__graph-align-top': Object.values(currentOrgExams).length < 4 }"
            >
                <div class="seats__graph-total">
                    {{ examLicenseMetrics.count }}
                    <span>Seats</span>
                </div>
                <Pie
                    :data="pieData"
                    class="seats__graph-pie"
                />
                <div class="seats__graph-legend">
                    <div v-if="examLicenseMetrics.active" class="seats__graph-legend-row seats__graph-legend-active">
                        <div>Active Seats</div>
                        <div>
                            <span>{{ Math.round(
                                (examLicenseMetrics.active / examLicenseMetrics.count) * 100
                            ) }}</span>%
                        </div>
                    </div>
                    <div
                        v-if="examLicenseMetrics.available"
                        class="seats__graph-legend-row seats__graph-legend-available"
                    >
                        <div>Available Seats</div>
                        <div>
                            <span>{{ Math.round(
                                (examLicenseMetrics.available / examLicenseMetrics.count) * 100
                            ) }}</span>%
                        </div>
                    </div>
                    <div v-if="examLicenseMetrics.pending" class="seats__graph-legend-row seats__graph-legend-pending">
                        <div>Pending Seats</div>
                        <div>
                            <span>{{ Math.round(
                                (examLicenseMetrics.pending / examLicenseMetrics.count) * 100
                            ) }}</span>%
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <PocketButton
            v-if="showPurchaseSeatsButton"
            class="seats__purchase-seats"
            type="secondary"
            @click="showPurchaseSeatsSidePanel"
        >
            + Purchase Seats
        </PocketButton>
    </div>
</template>

<style lang="scss" scoped>


.seats {
    margin-bottom: 70px;

    &__title {
        font-size: 16px;
        line-height: 22px;
        margin: 0 0 30px 11px;
        font-weight: 600;
    }

    &__data {
        display: flex;
        justify-content: space-between;
        position: relative;
    }

    &__table {
        width: 512px;
    }

    &__graph {
        width: 186px;
        position: relative;
    }

    &__graph-align-top {
        position: absolute;
        right: 0;
        bottom: 0;
    }

    &__graph-total {
        position: absolute;
        text-align: center;
        width: 100%;
        font-weight: 600;
        font-size: 34px;
        line-height: 34px;
        top: 71px;
        z-index: 2;

        span {
            font-size: 13px;
            line-height: 14px;
            left: 18px;
            letter-spacing: 0.2px;
            font-weight: 500;
            opacity: 0.7;
            display: block;
        }
    }

    &__graph-pie {
        width: 186px;
        height: 186px;
    }

    &__graph-legend {
        margin-top: 26px;
    }

    &__graph-legend-row {
        font-size: 13px;
        font-weight: 500;
        display: flex;
        justify-content: space-between;
        line-height: 20px;
        align-items: center;
        text-align: left;

        div:first-child::before {
            content: '';
            display: inline-block;
            margin-right: 8px;
            width: 8px;
            height: 8px;
            border-radius: 8px;
        }

        :last-child {
            text-align: right;

            span {
                font-weight: 600;
            }
        }
    }

    &__graph-legend-active :first-child::before {
        background: $green;
    }

    &__graph-legend-available :first-child::before {
        background: $electric-violet;
    }

    &__graph-legend-pending :first-child::before {
        background: $butterscotch;
    }
}
</style>
